import React, { useState, useEffect, useCallback } from 'react';
import { useContent } from '../../../contexts/ContentContext';
import { useAdminAuth } from '../../../contexts/AdminAuthContext';
import { api, getWedding, updateWedding } from '../../../lib/api';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import CmsCoupleSection from './CmsCoupleSection';
import CmsWelcomeSection from './CmsWelcomeSection';
import CmsMetaSection from './CmsMetaSection';
import CmsStorySectionEditor from './CmsStorySectionEditor';
import CmsScheduleSection from './CmsScheduleSection';
import CmsInfoSection from './CmsInfoSection';
import CmsRsvpSection from './CmsRsvpSection';
import CmsSettingsSection from './CmsSettingsSection';

const ContentEditorTab = ({ weddingId }) => {
  const { content: legacyContent, refreshContent: legacyRefresh } = useContent();
  const { token } = useAdminAuth();
  const [editableContent, setEditableContent] = useState(null);
  const [weddingData, setWeddingData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null); // 'success' | 'error' | null
  const [hasChanges, setHasChanges] = useState(false);

  // Wedding-specific fetch (when editing a specific wedding)
  const fetchWeddingContent = useCallback(async () => {
    if (!weddingId || !token) return;
    try {
      const data = await getWedding(token, weddingId);
      setWeddingData(data.wedding);
      const content = typeof data.wedding.content === 'string'
        ? JSON.parse(data.wedding.content)
        : data.wedding.content;
      setEditableContent(structuredClone(content));
      setHasChanges(false);
    } catch (err) {
      console.error('Failed to load wedding content:', err);
    }
  }, [weddingId, token]);

  // Wedding mode: fetch on mount
  useEffect(() => {
    if (weddingId) fetchWeddingContent();
  }, [fetchWeddingContent, weddingId]);

  // Legacy mode: sync from ContentContext
  useEffect(() => {
    if (!weddingId && legacyContent) {
      setEditableContent(structuredClone(legacyContent));
      setHasChanges(false);
    }
  }, [weddingId, legacyContent]);

  const handleChange = (updatedContent) => {
    setEditableContent(updatedContent);
    setHasChanges(true);
    setSaveStatus(null);
  };

  const handleSave = async () => {
    if (!editableContent || !token) return;
    setSaving(true);
    setSaveStatus(null);

    try {
      if (weddingId) {
        await updateWedding(token, weddingId, editableContent);
      } else {
        await api.updateContent(editableContent, token);
        await legacyRefresh();
      }
      setSaveStatus('success');
      setHasChanges(false);
      setTimeout(() => setSaveStatus(null), 3000);
    } catch (err) {
      console.error('Save failed:', err);
      setSaveStatus('error');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (weddingId) {
      await fetchWeddingContent();
    } else if (legacyContent) {
      setEditableContent(structuredClone(legacyContent));
    }
    setHasChanges(false);
    setSaveStatus(null);
  };

  if (!editableContent) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <Icon name="Loader2" size={32} className="animate-spin text-primary mx-auto mb-3" />
          <p className="text-muted-foreground">Loading content...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Sticky save bar */}
      <div className="sticky top-16 z-50 bg-card border border-border rounded-xl p-3 shadow-romantic-md flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Icon name="FileText" size={20} color="var(--color-primary)" />
          <span className="text-sm font-medium text-foreground">Content Editor</span>
          {hasChanges && (
            <span className="text-xs px-2 py-0.5 bg-warning/10 text-warning rounded-full">Unsaved changes</span>
          )}
          {saveStatus === 'success' && (
            <span className="text-xs px-2 py-0.5 bg-success/10 text-success rounded-full flex items-center gap-1">
              <Icon name="Check" size={12} /> Saved
            </span>
          )}
          {saveStatus === 'error' && (
            <span className="text-xs px-2 py-0.5 bg-error/10 text-error rounded-full">Save failed</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {hasChanges && (
            <Button variant="outline" size="sm" onClick={handleReset}>
              Discard
            </Button>
          )}
          <Button
            variant="default"
            size="sm"
            onClick={handleSave}
            loading={saving}
            disabled={!hasChanges}
            iconName="Save"
            iconPosition="left"
          >
            Save Changes
          </Button>
        </div>
      </div>

      {/* Editor sections */}
      <CmsCoupleSection content={editableContent} onChange={handleChange} weddingId={weddingId} weddingData={weddingData} token={token} />
      <CmsWelcomeSection content={editableContent} onChange={handleChange} />
      <CmsMetaSection content={editableContent} onChange={handleChange} weddingId={weddingId} />
      <CmsStorySectionEditor content={editableContent} onChange={handleChange} weddingId={weddingId} />
      <CmsScheduleSection content={editableContent} onChange={handleChange} />
      <CmsInfoSection content={editableContent} onChange={handleChange} />
      <CmsRsvpSection content={editableContent} onChange={handleChange} weddingId={weddingId} />
      <CmsSettingsSection content={editableContent} onChange={handleChange} />

      {/* Bottom save button */}
      <div className="flex justify-end gap-2 pb-8">
        {hasChanges && (
          <Button variant="outline" size="lg" onClick={handleReset}>
            Discard Changes
          </Button>
        )}
        <Button
          variant="default"
          size="lg"
          onClick={handleSave}
          loading={saving}
          disabled={!hasChanges}
          iconName="Save"
          iconPosition="left"
        >
          Save All Changes
        </Button>
      </div>
    </div>
  );
};

export default ContentEditorTab;
